import type { ApiErrorBody } from '@fleetcore/shared'

/** A failed HTTP call, carrying the server's stable machine-readable code. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: unknown
  readonly requestId: string | undefined

  constructor(status: number, code: string, message: string, body?: Partial<ApiErrorBody>) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = body?.details
    this.requestId = body?.requestId
  }

  static network(cause: unknown): ApiError {
    const error = new ApiError(0, 'network_error', 'The API could not be reached')
    error.cause = cause
    return error
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}

/**
 * Maps a failure onto an i18n key. The server's `message` is English developer
 * text by contract (ApiErrorBody) and must never reach a user.
 *
 * Note the deliberate absence of anything that distinguishes "does not exist"
 * from "belongs to another tenant": the API answers 404 for both, and the UI
 * repeats that indistinguishable answer rather than softening it.
 */
export function errorMessageKey(error: unknown): string {
  if (!isApiError(error)) return 'errors:http.unknown'

  // A platform account holds every permission and still cannot read tenant data:
  // scope comes from a signed claim, so it must impersonate a tenant first. The
  // generic 403 text tells that user they lack rights, which is both wrong and a
  // dead end — nothing they can grant themselves fixes it. Answer the actual
  // question instead.
  if (error.code === 'tenant_context_required') return 'errors:http.tenantContextRequired'

  switch (error.status) {
    case 0:
      return 'errors:http.network'
    case 400:
    case 422:
      return 'errors:http.badRequest'
    case 401:
      return 'errors:http.unauthorized'
    case 403:
      return 'errors:http.forbidden'
    case 404:
      return 'errors:http.notFound'
    case 409:
      return 'errors:http.conflict'
    case 429:
      return 'errors:http.tooManyRequests'
    case 503:
      return 'errors:http.unavailable'
    default:
      return error.status >= 500 ? 'errors:http.server' : 'errors:http.unknown'
  }
}
